export const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string
export const DEFAULT_MAP_CENTER: [number, number] = [115, 15] // Southeast Asia

// =============================================================================
// Map Zoom Levels (Centralized Configuration)
// =============================================================================
/** Default zoom level when map first loads */
export const DEFAULT_MAP_ZOOM = 4

/** Dashboard page specific zoom level (more zoomed out) */
export const DASHBOARD_MAP_ZOOM = 3

/** Zoom level when user clicks on a marker or location (detail view) */
export const MARKER_CLICK_ZOOM = 5

/** Default zoom level for flyTo function when zoom not specified */
export const FLYTO_DEFAULT_ZOOM = 8

/** Zoom level when flying to country selection (more moderate zoom) */
export const COUNTRY_FLYTO_ZOOM = 5

/** Maximum zoom level (fully zoomed in) */
export const MAP_MAX_ZOOM = 18

/** Minimum zoom level (fully zoomed out) */
export const MAP_MIN_ZOOM = 0

/**
 * Latitude offset to center popup (which appears above marker) in map view.
 * At zoom 9, this offset moves the map center north so the popup
 * appears centered while the marker point stays at the lower portion.
 * Larger value = popup more centered, marker more towards bottom.
 */
export const POPUP_CENTER_LAT_OFFSET = 0.5

// =============================================================================
// Default Values
// =============================================================================
export const DEFAULT_RISK_YEAR = 2050
export const DEFAULT_SSP_SCENARIO = 'ssp585' as const
export const DEFAULT_PRECIP_METRIC = 'one_day_precip_100yr' as const  // 100-year return period (mm/day)
export const DEFAULT_WIND_METRIC = 'rl_100yr' as const  // 50-year return level
export const DEFAULT_DROUGHT_METRIC = 'water_stress' as const  // Projected water stress ratio
export const DEFAULT_HAIL_METRIC = 'days_large_hail_mean' as const
export const DEFAULT_WILDFIRE_METRIC = 'FR_annualFireProbability_mean' as const
export const DEFAULT_FLOOD_METRIC = 'depthCombined100yr_mean' as const
export const DEFAULT_COLD_METRIC = 'CL_daysBelow0C_mean' as const

// =============================================================================
// Map Styles (Re-exported from mapStyles.ts for convenience)
// Full configuration available in @lib/mapStyles
// =============================================================================
export {
  MAP_STYLES,
  MAP_STYLE,
  THEME_MAP_STYLES,
  MAP_STYLE_OPTIONS,
  DEFAULT_MAP_STYLE_ID,
  getMapStyleById,
  getMapStyleUrl,
  type MapStyleId,
  type MapStyleOption,
} from './mapStyles'

// =============================================================================
// Clustering Configuration
// =============================================================================
// Conservative settings: only clusters visually overlapping points
export const CLUSTER_CONFIG = {
  /** Maximum zoom level at which clustering is enabled. Zoom > this shows individual points */
  maxZoom: 9,
  /** Radius of each cluster in pixels. Smaller = less aggressive clustering */
  radius: 20,
  /** Minimum points required to form a cluster */
  minPoints: 2,
} as const

/** Zoom level at which to switch from clusters to individual React markers */
export const INDIVIDUAL_MARKER_ZOOM = 8

// =============================================================================
// Cache Configuration (Centralized)
// =============================================================================
/**
 * Base cache TTL in milliseconds.
 * Keep in sync with backend CACHE_TTL_BASE in kcs/settings/base.py
 * 
 * Current: 24 hours (matches backend)
 */
export const CACHE_TTL_BASE = 10 * 24 * 60 * 60 * 1000  // 10 days in milliseconds

/** Frontend portfolio data cache TTL */
export const PORTFOLIO_CACHE_TTL = CACHE_TTL_BASE

// =============================================================================
// Map Display Limits
// =============================================================================
/**
 * Maximum number of points to display on the Dashboard map.
 * When data exceeds this limit, random sampling is applied.
 * This prevents performance issues with large datasets (10k+ points).
 *
 * Adjust this value based on device performance requirements.
 */
export const MAX_MAP_POINTS = 2000

/**
 * Maximum number of points for Portfolio page map.
 * Lower than Dashboard because Portfolio GeoJSON contains more data per point
 * (risk scores, occupancy, valuation, etc.) which increases payload size.
 *
 * IMPORTANT: This value is mirrored in backend/constants.py
 * Backend uses consistent sampling (order_by ID) to ensure same assets
 * are returned across geojson and risk-scores endpoints.
 */
export const MAX_PORTFOLIO_MAP_POINTS = 2000


